import { useRef, useState } from "react";
import type { AssinaturaForm } from "../lib/useAssinatura.ts";
import { AssinaturaCanvas } from "./AssinaturaCanvas.tsx";

/** Campos de "Minha assinatura", reusados no Sheet (mobile) e no Modal (desktop).
 *  Duas fontes para a MESMA assinatura: **desenhar na tela** (gera PNG
 *  transparente) ou **enviar uma imagem**. Ambas caem em `f.enviar` (presign→B2). */
export function AssinaturaCampos({ f }: { f: AssinaturaForm }) {
  const inputRef = useRef<HTMLInputElement>(null);
  // No celular, desenhar é o caminho natural → padrão.
  const [modo, setModo] = useState<"desenhar" | "enviar">("desenhar");

  const escolher = () => inputRef.current?.click();
  const aoSelecionar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = ""; // permite reenviar o mesmo arquivo
    if (arquivo) {
      void f.enviar(arquivo);
    }
  };

  return (
    <>
      <div className="m-field">
        <span className="spec" style={{ display: "block", marginBottom: 4 }}>
          Assinatura atual
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 72,
            padding: 8,
            border: "1px dashed var(--border, #cfd8e0)",
            borderRadius: 8,
            background: "var(--surface-2, #f4f7fa)",
          }}
        >
          {f.carregando ? (
            <span className="spec">Carregando…</span>
          ) : f.atual ? (
            <img
              src={f.atual.url}
              alt="Sua assinatura"
              style={{ maxHeight: 64, maxWidth: "100%", objectFit: "contain" }}
            />
          ) : (
            <span className="spec">Nenhuma assinatura enviada.</span>
          )}
        </div>
      </div>

      {/* Alternador de fonte: desenhar × enviar imagem */}
      <div className="m-seg" style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button
          type="button"
          className={`m-btn${modo === "desenhar" ? " primary" : ""}`}
          style={{ flex: 1 }}
          onClick={() => setModo("desenhar")}
        >
          ✍️ Desenhar
        </button>
        <button
          type="button"
          className={`m-btn${modo === "enviar" ? " primary" : ""}`}
          style={{ flex: 1 }}
          onClick={() => setModo("enviar")}
        >
          🖼️ Enviar imagem
        </button>
      </div>

      {modo === "desenhar" ? (
        <AssinaturaCanvas onConcluir={(arquivo) => void f.enviar(arquivo)} />
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={aoSelecionar}
          />
          <button type="button" className="m-btn primary" onClick={escolher} disabled={f.enviando}>
            {f.enviando ? "Enviando…" : f.atual ? "Trocar assinatura" : "Enviar assinatura"}
          </button>
          <p className="spec" style={{ marginTop: 8 }}>
            PNG, JPG ou WEBP (até 2 MB). Prefira PNG com fundo transparente.
          </p>
        </>
      )}

      {(f.atual || f.enviando) && (
        <div style={{ marginTop: 10 }}>
          {f.enviando && <span className="spec">Enviando…</span>}
          {f.atual && !f.enviando && (
            <button type="button" className="m-btn" onClick={() => void f.remover()}>
              Remover assinatura
            </button>
          )}
        </div>
      )}

      {f.erro && (
        <p className="m-alert danger" style={{ marginTop: 8 }}>
          {f.erro}
        </p>
      )}
      {f.sucesso && (
        <p
          className="m-alert"
          style={{ marginTop: 8, background: "var(--mat-3-soft)", color: "var(--mat-4)" }}
        >
          Assinatura salva com sucesso.
        </p>
      )}
      <p className="spec" style={{ marginTop: 8 }}>
        A assinatura aparece sobre a linha no Relatório da Unidade.
      </p>
    </>
  );
}
